import { useEffect } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ExpensesEntitiesExpensableResourceTypeEnum,
  UpdateExpensableMutationVariables,
  UpdateExpensesExpensableRatesInput,
  UpdateMileageMutationVariables,
} from 'generated/resources/graphql'
import { pick } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type ExpenseFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { type MileageFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { type PerDiemFormValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import { type LocalConcept } from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/DailyConcepts'
import { getNewFileUploads } from 'modules/expenses/components/expenses/Form/Buttons/helpers'
import { buildErrorMessages } from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import {
  isExpenseFormValues,
  isMileageFormValues,
  isPerDiemFormValues,
  validatePaymentOption,
} from 'modules/expenses/components/expenses/Form/Buttons/helpers/typechecks'
import {
  normalizeEffectiveOn,
  normalizeTaxes,
  useUpdate,
  useUpdateMileage,
} from 'modules/expenses/hooks/useExpensable'
import { CustomizationField, Tax } from 'modules/expenses/hooks/useExpensesCustomization'
import {
  SignableOrganizationPerCountry,
  getSignedForOrganization,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { isDraftExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

interface UpdateButtonProps {
  formType: ExpensesEntitiesExpensableResourceTypeEnum
  expensable: Expensable
  formValues: ExpenseFormValues | PerDiemFormValues | MileageFormValues
  formIsDirty: boolean
  customizationFields: CustomizationField[]
  canBeReviewed: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  onClose: () => void
  onLoadingChange?: (loading: boolean) => void
  disabled?: boolean
  isNewPerDiem?: boolean
  modifiedConcepts?: LocalConcept[] | null
}

// Type guards for Mutation Variables

const isMileageData = (
  data: UpdateExpensableMutationVariables | UpdateMileageMutationVariables,
  formType: string
): data is UpdateMileageMutationVariables => {
  return formType === 'mileage'
}

// This type check considers expenses and per diem because both of them are using the same UpdateExpensableMutation
// mutation.

const isExpensableData = (
  data: UpdateExpensableMutationVariables | UpdateMileageMutationVariables,
  formType: string
): data is UpdateExpensableMutationVariables => {
  return formType === 'expense' || formType === 'perdiem'
}

interface NewPerDiemFormPerDiem {
  perDiemDestinations?: Array<{
    id: string | number
    locationName: string
    country: string
    administrativeAreaLevel1?: string | null
    administrativeAreaLevel2?: string | null
    locality?: string | null
    arrivalDate?: string | null
  }>
  startDate?: string | null
  endDate?: string | null
  tripName?: string | null
}

function hasNewPerDiemFormPerDiem(
  values: PerDiemFormValues
): values is PerDiemFormValues & { perDiem: NewPerDiemFormPerDiem } {
  return (
    values != null &&
    typeof values === 'object' &&
    'perDiem' in values &&
    values.perDiem != null &&
    typeof values.perDiem === 'object'
  )
}

const UpdateExpenseFields = [
  'employeeId',
  'currency',
  'category',
  'subcategory',
  'documentNumber',
  'description',
  'reference',
  'merchantName',
  'merchantTin',
  'paymentMethod',
  'reimbursableCurrency',
  'exchangeRate',
]

const UpdatePerDiemFields = [
  'currency',
  'category',
  'subcategory',
  'description',
  'internalReference',
  'budgetId',
  'start_date',
  'end_date',
  'from',
  'to',
  'groupId',
  'perDiemRates',
  'projects',
  'files',
]

const UpdateMileageFields = [
  'budgetId',
  'categoryId',
  'currency',
  'description',
  'employeeId',
  'files',
  'from',
  'internalReference',
  'projects',
  'rate',
  'subcategory',
  'to',
  'units',
]

interface Project {
  projectWorker?: { id: number } | null
  subproject?: { id?: number | null } | null
}

const formatProjects = (projects: Project[]) => {
  return projects
    .filter((project) => project.projectWorker?.id)
    .map((project) => ({
      projectWorkerId: project.projectWorker?.id || 0,
      subprojectId: project.subproject?.id || undefined,
    }))
}

const UpdateButton = ({
  formType,
  expensable,
  formValues,
  formIsDirty,
  customizationFields,
  canBeReviewed,
  taxAgencyOrganization,
  onClose,
  onLoadingChange,
  disabled,
  isNewPerDiem = false,
  modifiedConcepts = null,
}: UpdateButtonProps) => {
  const { company, employee } = useCurrentSession()
  const isDraft = isDraftExpensable(expensable)

  const submitStrategies = {
    expense: (values: ExpenseFormValues | PerDiemFormValues): UpdateExpensableMutationVariables => {
      if (!('taxes' in values)) {
        throw new Error('Expense strategy requires ExpenseFormValues')
      }

      const formData = pick(values, [...UpdateExpenseFields])
      const filesData = getNewFileUploads(values.files)

      const taxes = values.taxes.map((tax: Tax) => {
        return {
          amount: toCents(tax.amount ?? 0),
          base_amount: toCents(tax.base_amount ?? 0),
          percentage: tax.percentage ?? 0,
        }
      })

      const signedForOrganization = getSignedForOrganization({
        filesData: values?.files || [],
        expensable,
        filesIdToRemove: values.filesIdToRemove,
        taxAgencyOrganization,
      })
      const employeeId = values?.employeeId || 0
      const isAutomatic = values.creationType === 'automatic'
      const merchantName = isAutomatic ? expensable?.expense?.merchantName : values.merchantName

      return {
        ...formData,
        id: expensable.id,
        resourceType: 'expense',
        documentType: values.documentType,
        amount: toCents(values.amount),
        employeeId: employeeId,
        companyId: company.id,
        effectiveOn: normalizeEffectiveOn(values.effectiveOn),
        merchantName: merchantName,
        userMerchant: values.merchantName,
        taxes: normalizeTaxes({
          taxes: taxes,
          vatAmount: taxes[0]?.amount ?? null,
          customizationFields,
        }),
        projects: values.projects.map((project) => ({
          projectWorkerId: project.projectWorker?.id ?? 0,
          subprojectId: project.subproject?.id,
        })),
        budgetId: values.budgetId,
        reimbursableAmount: values?.reimbursableAmount ? toCents(values.reimbursableAmount) : null,
        reimbursableCurrency: values.reimbursableCurrency,
        files: filesData,
        filesIdToRemove: values.filesIdToRemove,
        signedForOrganization: signedForOrganization,
        payment: validatePaymentOption(values.payment),
        internalReference: values.internalReference,
        costCenters: values.cost_centers,
      }
    },

    perdiem: (values: ExpenseFormValues | PerDiemFormValues): UpdateExpensableMutationVariables => {
      if (!('perDiemRates' in values)) {
        throw new Error('PerDiem strategy requires PerDiemFormValues')
      }

      const formatRates = (
        rates: PerDiemFormValues['perDiemRates']
      ): UpdateExpensesExpensableRatesInput[] => {
        if (!rates) return []

        // Filter out rates with null or undefined rateConfigurationId
        // This is because the backend expects a rateConfigurationId defined, but we start with an empty rate as a placeholder
        // The user can still submit with the empty rate to save as draft, but we need to filter it out before sending to the backend
        return rates
          .filter(
            (rate): rate is NonNullable<typeof rate> & { rateConfigurationId: number } =>
              rate.rateConfigurationId !== null && rate.rateConfigurationId !== undefined
          )
          .map(
            (rate): UpdateExpensesExpensableRatesInput => ({
              id: rate.id,
              rateConfigurationId: rate.rateConfigurationId ?? 0,
              totalAmountCents: rate.totalAmount || 0,
              totalDays: rate.units || 0,
            })
          )
      }

      const formData = pick(values, [...UpdatePerDiemFields])
      const filesData = getNewFileUploads(values.files)

      const employeeId = values?.employeeId || 0

      // Calculate amount from perDiemRates
      const calculatedAmount =
        values.perDiemRates?.reduce((acc, rate) => acc + rate.totalAmount, 0) || 0

      // For new per diem wizard, use form's amount field (already an integer)
      // Otherwise use calculatedAmount from rates, or parse expensable amount as fallback
      const amount =
        'amount' in values && typeof values.amount === 'number'
          ? values.amount
          : calculatedAmount || parseInt(String(expensable?.amount || '0'), 10)

      // Use new per diem format when wizard is enabled
      if (isNewPerDiem) {
        const formPerDiem = hasNewPerDiemFormPerDiem(values) ? values.perDiem : undefined
        const rawDestinations =
          formPerDiem?.perDiemDestinations ?? expensable.perDiem?.perDiemDestinations ?? []
        const destinations = rawDestinations.map((dest) => {
          const numericId =
            typeof dest.id === 'number'
              ? dest.id
              : Number.isInteger(Number(dest.id))
                ? Number(dest.id)
                : null
          return {
            ...(numericId != null && { id: numericId }),
            locationName: dest.locationName,
            country: dest.country,
            administrativeAreaLevel1: dest.administrativeAreaLevel1,
            administrativeAreaLevel2: dest.administrativeAreaLevel2,
            locality: dest.locality,
            arrivalDate: dest.arrivalDate,
          }
        })

        // Use modified concepts if available, otherwise get from existing per diem
        const perDiem = expensable.perDiem
        const conceptsToSend =
          modifiedConcepts ||
          perDiem?.perDiemConcepts?.map((concept) => ({
            id: concept.id,
            date: concept.date,
            breakfastProvided: concept.breakfastProvided,
            lunchProvided: concept.lunchProvided,
            dinnerProvided: concept.dinnerProvided,
            accommodationProvided: concept.accommodationProvided,
          }))

        const result: UpdateExpensableMutationVariables = {
          ...formData,
          id: expensable.id,
          resourceType: 'perdiem',
          amount: amount,
          employeeId: employeeId,
          companyId: company.id,
          effectiveOn: normalizeEffectiveOn(formPerDiem?.startDate ?? values.start_date ?? ''),
          projects: formatProjects(values.projects),
          budgetId: values.budgetId,
          files: filesData,
          filesIdToRemove: values.filesIdToRemove || [],
          internalReference: values.internalReference,
          description: values.description,
          perDiem: undefined,
          perDiemNew: {
            budgetId: values.budgetId,
            currency: values.currency,
            companyId: company.id,
            employeeId: employeeId,
            endDate: formPerDiem?.endDate ?? values.end_date ?? '',
            files: filesData,
            filesIdToRemove: values.filesIdToRemove || [],
            startDate: formPerDiem?.startDate ?? values.start_date ?? '',
            subcategory: values.subcategory,
            description: values.description,
            projects: formatProjects(values.projects),
            destinations: destinations,
            tripName: formPerDiem?.tripName ?? undefined,
            concepts: conceptsToSend?.map((concept) => ({
              id: concept.id || undefined,
              date: concept.date,
              breakfastProvided: concept.breakfastProvided,
              lunchProvided: concept.lunchProvided,
              dinnerProvided: concept.dinnerProvided,
              accommodationProvided: concept.accommodationProvided,
            })),
          },
          taxes: [],
          costCenters: values.cost_centers,
        }
        return result
      }

      return {
        ...formData,
        id: expensable.id,
        resourceType: 'perdiem',
        amount: amount,
        employeeId: employeeId,
        companyId: company.id,
        effectiveOn: normalizeEffectiveOn(values.start_date),
        projects: formatProjects(values.projects),
        budgetId: values.budgetId,
        files: filesData,
        filesIdToRemove: values.filesIdToRemove || [],
        internalReference: values.internalReference,
        description: values.description,
        perDiem: {
          amount: amount,
          budgetId: values.budgetId,
          currency: values.currency,
          companyId: company.id,
          employeeId: employeeId,
          endDate: values.end_date,
          files: filesData,
          filesIdToRemove: values.filesIdToRemove || [],
          from: values.from,
          projects: formatProjects(values.projects),
          deletedRates: values.deleted_rates,
          startDate: values.start_date,
          subcategory: values.subcategory,
          to: values.to,
          rates: formatRates(values.perDiemRates),
          description: values.description,
        },
        taxes: [],
        costCenters: values.cost_centers,
      }
    },
    mileage: (values: MileageFormValues): UpdateMileageMutationVariables => {
      const formData = pick(values, [...UpdateMileageFields])
      const originLatitude = values.originLatitude ? parseFloat(values.originLatitude) : null
      const originLongitude = values.originLongitude ? parseFloat(values.originLongitude) : null
      const destinationLatitude = values.destinationLatitude
        ? parseFloat(values.destinationLatitude)
        : null
      const destinationLongitude = values.destinationLongitude
        ? parseFloat(values.destinationLongitude)
        : null
      const roundTrip = values.roundTrip ?? false
      const amount = toCents(values.amount)
      const calculatedMileage = toCents(values.calculatedMileage)
      const mileage = toCents(values.mileage)
      const filesData = getNewFileUploads(values.files)
      const effectiveOn = normalizeEffectiveOn(values.effectiveOn)
      const employeeId = values?.employeeId || 0
      const stops = values.stops?.map(
        (
          stop: { name?: string | null; latitude?: string | null; longitude?: string | null },
          i: number
        ) => ({
          name: stop.name || '',
          latitude: stop.latitude ? parseFloat(stop.latitude) : null,
          longitude: stop.longitude ? parseFloat(stop.longitude) : null,
          position: i,
        })
      )

      return {
        ...formData,
        id: expensable.id,
        resourceType: 'mileage',
        amount: amount,
        calculatedMileage: calculatedMileage,
        employeeId: employeeId,
        mileage: mileage,
        originLatitude: originLatitude,
        originLongitude: originLongitude,
        destinationLatitude: destinationLatitude,
        destinationLongitude: destinationLongitude,
        roundTrip: roundTrip,
        projects: formatProjects(values.projects),
        files: filesData,
        filesIdToRemove: values.filesIdToRemove,
        from: values.from,
        internalReference: values.internalReference,
        rate: values.rate?.toString(),
        effectiveOn: effectiveOn,
        subcategory: values.subcategory,
        to: values.to,
        units: values.units,
        costCenters: values.cost_centers,
        stops,
      }
    },
  }

  const buildSubmitData = ():
    | UpdateExpensableMutationVariables
    | UpdateMileageMutationVariables => {
    if (formType === 'expense' && isExpenseFormValues(formValues, formType)) {
      return submitStrategies.expense(formValues)
    } else if (formType === 'perdiem' && isPerDiemFormValues(formValues, formType)) {
      return submitStrategies.perdiem(formValues)
    } else if (formType === 'mileage' && isMileageFormValues(formValues, formType)) {
      return submitStrategies.mileage(formValues)
    }

    throw new Error(`No strategy found for form type: ${formType}`)
  }

  const { update, loading } = useUpdate()
  const { updateMileage, loading: loadingMileage } = useUpdateMileage()

  useEffect(() => {
    onLoadingChange?.(loading || loadingMileage)
  }, [loading, loadingMileage, onLoadingChange])

  const handleUpdateMileage = async (submitData: UpdateMileageMutationVariables) => {
    const result = await updateMileage(submitData)

    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })

    if (!handled.success) return handled
    const updatedExpensable = handled.data?.expensable
    if (!updatedExpensable) return handled

    app.setFlash({
      type: 'correct',
      message: isDraft
        ? i18n.t('expenses_v2.modals.new_expense.saved_as_draft_successfully')
        : i18n.t('expenses_v2.modals.new_expense.updated_successfully'),
    })

    onClose()
  }

  const handleUpdateExpensable = async (submitData: UpdateExpensableMutationVariables) => {
    const result = await update(submitData)

    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })

    if (!handled.success) return handled
    const updatedExpensable = handled.data?.expensable
    if (!updatedExpensable) return handled

    app.setFlash({
      type: 'correct',
      message: isDraft
        ? i18n.t('expenses_v2.modals.new_expense.saved_as_draft_successfully')
        : i18n.t('expenses_v2.modals.new_expense.updated_successfully'),
    })

    onClose()
  }

  const updateExpensable = async () => {
    const submitData = buildSubmitData()

    if (formType === 'mileage' && isMileageData(submitData, formType)) {
      return await handleUpdateMileage(submitData)
    } else if (
      (formType === 'expense' || formType === 'perdiem') &&
      isExpensableData(submitData, formType)
    ) {
      return await handleUpdateExpensable(submitData)
    }

    throw new Error(`No strategy found for form type: ${formType}`)
  }

  return (
    <Button
      disabled={loading || !formIsDirty || disabled}
      type={expensable.groupId && !canBeReviewed ? 'primary' : 'secondary'}
      onClick={updateExpensable}
      loading={loading}
      iconName={loading ? 'upload' : undefined}
    >
      {i18n.t('expenses_v2.buttons.save')}
    </Button>
  )
}

export default UpdateButton
